import React, { useEffect, useState, useCallback } from 'react';
import { mcxApi, clientsApi } from '../../lib/api';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { StatusBadge } from '../shared/StatusBadge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';
import { 
  RefreshCw, Plus, Edit, Trash2, FileSpreadsheet, FileText, 
  TrendingUp, TrendingDown, DollarSign, Package, Users, CheckCircle,
  Calculator, BarChart3, Printer, Upload, Clock, Play, History, Table2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { WeeklyDollarPage } from './WeeklyDollarPage';
import { MCXSettlementPage } from './MCXSettlementPage';

const EXCHANGES = ['MCX', 'NSE', 'BSE'];
const TRADE_TYPES = ['BUY', 'SELL'];

export function MCXPage() {
  const [activeTab, setActiveTab] = useState('scripts');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [scripts, setScripts] = useState([]);
  const [mcxClients, setMcxClients] = useState([]);
  const [mainClients, setMainClients] = useState([]);
  const [trades, setTrades] = useState([]);
  const [positions, setPositions] = useState([]);
  const [settlements, setSettlements] = useState([]);
  
  // Dialog states
  const [scriptDialog, setScriptDialog] = useState({ open: false, mode: 'add', data: null });
  const [clientDialog, setClientDialog] = useState({ open: false, mode: 'add', data: null });
  const [tradeDialog, setTradeDialog] = useState({ open: false, mode: 'add', data: null });
  const [settleDialog, setSettleDialog] = useState({ open: false, position: null });
  const [priceDialog, setPriceDialog] = useState({ open: false, script: null });
  const [bhavCopyDialog, setBhavCopyDialog] = useState({ open: false });
  
  // Bhav Copy state
  const [bhavCopyStatus, setBhavCopyStatus] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [csvContent, setCsvContent] = useState('');
  
  // Filters
  const [scriptFilter, setScriptFilter] = useState({ status: '', exchange: '' });
  const [clientFilter, setClientFilter] = useState({ status: '' });
  const [tradeFilter, setTradeFilter] = useState({ mcx_client_id: '', script_id: '', is_settled: '' });
  
  // Forms
  const [scriptForm, setScriptForm] = useState({
    script_name: '', exchange: 'MCX', contract_name: '', expiry_date: '',
    lot_size: 1, tick_size: 0.01, last_price: 0
  });
  const [clientForm, setClientForm] = useState({
    client_name: '', id_name: '', brokerage_per_lot: 0, brokerage_remittance: 0,
    patti_percent: 0, limit: 0, contact_number: '', linked_client_id: ''
  });
  const [tradeForm, setTradeForm] = useState({
    trade_date: new Date().toISOString().split('T')[0], mcx_client_id: '', script_id: '',
    trade_type: 'BUY', rate: '', quantity: 1, remarks: ''
  });
  const [settleForm, setSettleForm] = useState({ settlement_price: '', settlement_type: 'MANUAL' });
  const [priceForm, setPriceForm] = useState({ price: '' });

  // Helper to clean empty filter values
  const cleanFilter = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [scriptsData, mcxClientsData, mainClientsData, tradesData, positionsData, settlementsData, bhavStatus] = await Promise.all([
        mcxApi.scripts.list(cleanFilter(scriptFilter)),
        mcxApi.clients.list(cleanFilter(clientFilter)),
        clientsApi.list(),
        mcxApi.trades.list(cleanFilter(tradeFilter)),
        mcxApi.positions.list(),
        mcxApi.settlements.list(),
        mcxApi.bhavCopy.status().catch(() => null)
      ]);
      setScripts(scriptsData);
      setMcxClients(mcxClientsData);
      setMainClients(mainClientsData);
      setTrades(tradesData);
      setPositions(positionsData);
      setSettlements(settlementsData);
      setBhavCopyStatus(bhavStatus);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [scriptFilter, clientFilter, tradeFilter]);

  // Fetch price history
  const fetchPriceHistory = useCallback(async () => {
    try {
      const history = await mcxApi.bhavCopy.priceHistory({ limit: 50 });
      setPriceHistory(history);
    } catch (error) {
      console.error('Failed to fetch price history:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============ SCRIPT HANDLERS ============
  const openScriptDialog = (mode, data = null) => {
    if (mode === 'edit' && data) {
      setScriptForm({
        script_name: data.script_name || '',
        exchange: data.exchange || 'MCX',
        contract_name: data.contract_name || '',
        expiry_date: data.expiry_date?.split('T')[0] || '',
        lot_size: data.lot_size || 1,
        tick_size: data.tick_size || 0.01,
        last_price: data.last_price || 0
      });
    } else {
      setScriptForm({
        script_name: '', exchange: 'MCX', contract_name: '', expiry_date: '',
        lot_size: 1, tick_size: 0.01, last_price: 0
      });
    }
    setScriptDialog({ open: true, mode, data });
  };

  const handleSaveScript = async () => {
    try {
      if (!scriptForm.script_name || !scriptForm.contract_name || !scriptForm.expiry_date) {
        toast.error('Please fill required fields');
        return;
      }
      if (scriptDialog.mode === 'add') {
        await mcxApi.scripts.create(scriptForm);
        toast.success('Script created');
      } else {
        await mcxApi.scripts.update(scriptDialog.data.script_id, scriptForm);
        toast.success('Script updated');
      }
      setScriptDialog({ open: false, mode: 'add', data: null });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to save script');
    }
  };

  const handleDeleteScript = async (script) => {
    if (!window.confirm(`Delete script "${script.script_name}"?`)) return;
    try {
      await mcxApi.scripts.delete(script.script_id);
      toast.success('Script deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Cannot delete script with trades');
    }
  };

  const openPriceDialog = (script) => {
    setPriceForm({ price: script.last_price || '' });
    setPriceDialog({ open: true, script });
  };

  const handleUpdatePrice = async () => {
    try {
      const price = parseFloat(priceForm.price);
      if (isNaN(price) || price <= 0) {
        toast.error('Enter valid price');
        return;
      }
      await mcxApi.scripts.updatePrice(priceDialog.script.script_id, price);
      toast.success('Price updated');
      setPriceDialog({ open: false, script: null });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to update price');
    }
  };

  // ============ CLIENT HANDLERS ============
  const openClientDialog = (mode, data = null) => {
    if (mode === 'edit' && data) {
      setClientForm({
        client_name: data.client_name || '',
        id_name: data.id_name || '',
        brokerage_per_lot: data.brokerage_per_lot || 0,
        brokerage_remittance: data.brokerage_remittance || 0,
        patti_percent: data.patti_percent || 0,
        limit: data.limit || 0,
        contact_number: data.contact_number || '',
        linked_client_id: data.linked_client_id || ''
      });
    } else {
      setClientForm({
        client_name: '', id_name: '', brokerage_per_lot: 0, brokerage_remittance: 0,
        patti_percent: 0, limit: 0, contact_number: '', linked_client_id: ''
      });
    }
    setClientDialog({ open: true, mode, data });
  };

  const handleSaveClient = async () => {
    try {
      if (!clientForm.client_name || !clientForm.id_name) {
        toast.error('Name and ID are required');
        return;
      }
      if (clientDialog.mode === 'add') {
        await mcxApi.clients.create(clientForm);
        toast.success('Client created');
      } else {
        await mcxApi.clients.update(clientDialog.data.mcx_client_id, clientForm);
        toast.success('Client updated');
      }
      setClientDialog({ open: false, mode: 'add', data: null });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to save client');
    }
  };

  // ============ TRADE HANDLERS ============
  const openTradeDialog = (mode, data = null) => {
    if (mode === 'edit' && data) {
      setTradeForm({
        trade_date: data.trade_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        mcx_client_id: data.mcx_client_id || '',
        script_id: data.script_id || '',
        trade_type: data.trade_type || 'BUY',
        rate: data.rate || '',
        quantity: data.quantity || 1,
        remarks: data.remarks || ''
      });
    } else {
      setTradeForm({
        trade_date: new Date().toISOString().split('T')[0], mcx_client_id: '', script_id: '',
        trade_type: 'BUY', rate: '', quantity: 1, remarks: ''
      });
    }
    setTradeDialog({ open: true, mode, data });
  };

  const handleSaveTrade = async () => {
    try {
      if (!tradeForm.mcx_client_id || !tradeForm.script_id || !tradeForm.rate || !tradeForm.quantity) {
        toast.error('Please fill all required fields');
        return;
      }
      const data = {
        ...tradeForm,
        rate: parseFloat(tradeForm.rate),
        quantity: parseInt(tradeForm.quantity)
      };
      if (tradeDialog.mode === 'add') {
        await mcxApi.trades.create(data);
        toast.success('Trade created');
      } else {
        await mcxApi.trades.update(tradeDialog.data.trade_id, data);
        toast.success('Trade updated');
      }
      setTradeDialog({ open: false, mode: 'add', data: null });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to save trade');
    }
  };

  const handleDeleteTrade = async (trade) => {
    if (trade.is_settled) {
      toast.error('Cannot delete settled trade');
      return;
    }
    if (!window.confirm('Delete this trade?')) return;
    try {
      await mcxApi.trades.delete(trade.trade_id);
      toast.success('Trade deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete trade');
    }
  };

  // ============ SETTLEMENT HANDLERS ============
  const openSettleDialog = (position) => {
    const script = scripts.find(s => s.script_id === position.script_id);
    setSettleForm({ 
      settlement_price: script?.last_price || position.current_price || '', 
      settlement_type: 'MANUAL' 
    });
    setSettleDialog({ open: true, position });
  };

  const handleSettle = async () => {
    try {
      const price = parseFloat(settleForm.settlement_price);
      if (isNaN(price) || price <= 0) {
        toast.error('Enter valid settlement price');
        return;
      }
      await mcxApi.settlements.settle({
        mcx_client_id: settleDialog.position.mcx_client_id,
        script_id: settleDialog.position.script_id,
        settlement_price: price,
        settlement_type: settleForm.settlement_type
      });
      toast.success('Position settled');
      setSettleDialog({ open: false, position: null });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Settlement failed');
    }
  };

  // ============ EXPORT HANDLERS ============
  const exportToExcel = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${filename}_${format(new Date(), 'yyyyMMdd')}.xlsx`);
    toast.success('Excel exported');
  };

  const exportToPDF = (title, columns, data) => {
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(16);
    doc.setTextColor(212, 175, 55);
    doc.text(title, 14, 15);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    
    autoTable(doc, {
      head: [columns],
      body: data,
      startY: 28,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] }
    });
    
    doc.save(`${title.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`);
    toast.success('PDF exported');
  };

  // ============ BHAV COPY HANDLERS ============
  const handleBhavCopyUpload = async () => {
    if (!csvContent.trim()) {
      toast.error('Please paste CSV content');
      return;
    }
    try {
      const result = await mcxApi.bhavCopy.uploadCsv(csvContent);
      toast.success(`Updated ${result.updated} scripts`);
      if (result.errors?.length > 0) {
        toast.warning(`${result.errors.length} errors occurred`);
      }
      setCsvContent('');
      setBhavCopyDialog({ open: false });
      fetchData();
      fetchPriceHistory();
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    }
  };

  const handleTriggerScheduled = async () => {
    if (!bhavCopyStatus?.can_trigger_scheduled) {
      toast.error('Only SuperAdmin can trigger scheduled updates');
      return;
    }
    try {
      const result = await mcxApi.bhavCopy.triggerScheduled();
      toast.success(`Scheduled update complete: ${result.updated || 0} scripts updated`);
      fetchData();
      fetchPriceHistory();
    } catch (error) {
      toast.error(error.message || 'Trigger failed');
    }
  };

  const handleBulkPriceUpdate = async () => {
    // Build prices array from active scripts with new prices
    const prices = scripts
      .filter(s => s.status === 'ACTIVE' && s._newPrice)
      .map(s => ({ script_id: s.script_id, price: parseFloat(s._newPrice) }));
    
    if (prices.length === 0) {
      toast.error('No prices to update');
      return;
    }
    
    try {
      const result = await mcxApi.bhavCopy.bulkUpdate(prices);
      toast.success(`Updated ${result.updated} scripts`);
      fetchData();
      fetchPriceHistory();
    } catch (error) {
      toast.error(error.message || 'Bulk update failed');
    }
  };

  // Calculate totals
  const totalMTM = positions.reduce((sum, p) => sum + (p.mtm || 0), 0);
  const openPositionsCount = positions.filter(p => p.net_quantity !== 0).length;
  const unsettledTradesCount = trades.filter(t => !t.is_settled).length;

  return (
    <div className="h-full flex flex-col space-y-3" data-testid="mcx-page">
      {/* Header Stats */}
      <div className="grid grid-cols-5 gap-3">
        <div className="bg-card border rounded p-3 text-center">
          <div className="text-xs text-muted-foreground">Scripts</div>
          <div className="text-xl font-bold text-primary">{scripts.filter(s => s.status === 'ACTIVE').length}</div>
        </div>
        <div className="bg-card border rounded p-3 text-center">
          <div className="text-xs text-muted-foreground">MCX Clients</div>
          <div className="text-xl font-bold text-primary">{mcxClients.filter(c => c.status === 'ACTIVE').length}</div>
        </div>
        <div className="bg-card border rounded p-3 text-center">
          <div className="text-xs text-muted-foreground">Open Trades</div>
          <div className="text-xl font-bold text-amber-500">{unsettledTradesCount}</div>
        </div>
        <div className="bg-card border rounded p-3 text-center">
          <div className="text-xs text-muted-foreground">Open Positions</div>
          <div className="text-xl font-bold text-blue-500">{openPositionsCount}</div>
        </div>
        <div className="bg-card border rounded p-3 text-center">
          <div className="text-xs text-muted-foreground">Total MTM</div>
          <div className={cn("text-xl font-bold font-mono", totalMTM >= 0 ? 'text-emerald-500' : 'text-red-500')}>
            {totalMTM.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="flex items-center justify-between">
          <TabsList className="flex-wrap h-auto gap-0.5">
            <TabsTrigger value="scripts" data-testid="tab-scripts">
              <Package className="h-4 w-4 mr-1" />Script Master
            </TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients">
              <Users className="h-4 w-4 mr-1" />Client Master
            </TabsTrigger>
            <TabsTrigger value="trades" data-testid="tab-trades">
              <TrendingUp className="h-4 w-4 mr-1" />Trade Entry
            </TabsTrigger>
            <TabsTrigger value="positions" data-testid="tab-positions">
              <BarChart3 className="h-4 w-4 mr-1" />Positions
            </TabsTrigger>
            <TabsTrigger value="settlements" data-testid="tab-book-settlement">
              <DollarSign className="h-4 w-4 mr-1" />Book Settlement
            </TabsTrigger>
            <TabsTrigger value="mcx-settlement" data-testid="tab-mcx-settlement">
              <Calculator className="h-4 w-4 mr-1" />MCX Settlement
            </TabsTrigger>
            <TabsTrigger value="weekly-dollar" data-testid="tab-weekly-dollar" className="text-primary">
              <Table2 className="h-4 w-4 mr-1" />Weekly$
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">
              <FileText className="h-4 w-4 mr-1" />Reports
            </TabsTrigger>
            <TabsTrigger value="agent-settings" data-testid="tab-agent-settings" onClick={() => fetchPriceHistory()}>
              <Clock className="h-4 w-4 mr-1" />Agent Settings
            </TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {/* Scripts Tab */}
        <TabsContent value="scripts" className="flex-1 mt-3">
          <GroupBox label="Script Master">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Select value={scriptFilter.status || 'all'} onValueChange={(v) => setScriptFilter({...scriptFilter, status: v === 'all' ? '' : v})}>
                  <SelectTrigger className="h-8 w-32"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="EXPIRED">Expired</SelectItem>
                    <SelectItem value="SETTLED">Settled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={scriptFilter.exchange || 'all'} onValueChange={(v) => setScriptFilter({...scriptFilter, exchange: v === 'all' ? '' : v})}>
                  <SelectTrigger className="h-8 w-28"><SelectValue placeholder="Exchange" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {EXCHANGES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => openScriptDialog('add')} data-testid="add-script-btn">
                  <Plus className="h-4 w-4 mr-1" />Add Script
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportToExcel(scripts, 'scripts')}>
                  <FileSpreadsheet className="h-4 w-4 mr-1" />Excel
                </Button>
              </div>
            </div>
            <div className="max-h-[calc(100vh-320px)] overflow-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left border">Script</th>
                    <th className="px-2 py-1.5 text-left border">Exchange</th>
                    <th className="px-2 py-1.5 text-left border">Contract</th>
                    <th className="px-2 py-1.5 text-center border">Expiry</th>
                    <th className="px-2 py-1.5 text-right border">Lot Size</th>
                    <th className="px-2 py-1.5 text-right border">Last Price</th>
                    <th className="px-2 py-1.5 text-center border">Status</th>
                    <th className="px-2 py-1.5 text-center border w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scripts.map(script => (
                    <tr key={script.script_id} className="border-b hover:bg-muted/50">
                      <td className="px-2 py-1 border font-medium">{script.script_name}</td>
                      <td className="px-2 py-1 border">{script.exchange}</td>
                      <td className="px-2 py-1 border">{script.contract_name}</td>
                      <td className="px-2 py-1 border text-center">{script.expiry_date?.split('T')[0]}</td>
                      <td className="px-2 py-1 border text-right">{script.lot_size}</td>
                      <td className="px-2 py-1 border text-right font-mono">
                        <button className="text-primary hover:underline" onClick={() => openPriceDialog(script)}>
                          {script.last_price?.toFixed(2) || '0.00'}
                        </button>
                      </td>
                      <td className="px-2 py-1 border text-center"><StatusBadge status={script.status} /></td>
                      <td className="px-2 py-1 border text-center">
                        <div className="flex justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openScriptDialog('edit', script)}>
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDeleteScript(script)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {scripts.length === 0 && (
                    <tr><td colSpan={8} className="px-2 py-4 text-center text-muted-foreground">No scripts found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </GroupBox>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="flex-1 mt-3">
          <GroupBox label="MCX Client Master">
            <div className="flex items-center justify-between mb-3">
              <Select value={clientFilter.status || 'all'} onValueChange={(v) => setClientFilter({status: v === 'all' ? '' : v})}>
                <SelectTrigger className="h-8 w-32"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => openClientDialog('add')} data-testid="add-client-btn">
                  <Plus className="h-4 w-4 mr-1" />Add Client
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportToExcel(mcxClients, 'mcx_clients')}>
                  <FileSpreadsheet className="h-4 w-4 mr-1" />Excel
                </Button>
              </div>
            </div>
            <div className="max-h-[calc(100vh-320px)] overflow-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left border">Client Name</th>
                    <th className="px-2 py-1.5 text-left border">ID Name</th>
                    <th className="px-2 py-1.5 text-right border">Brokerage/Lot</th>
                    <th className="px-2 py-1.5 text-right border">Remittance %</th>
                    <th className="px-2 py-1.5 text-right border">Patti %</th>
                    <th className="px-2 py-1.5 text-right border">Limit</th>
                    <th className="px-2 py-1.5 text-left border">Linked Client</th>
                    <th className="px-2 py-1.5 text-center border">Status</th>
                    <th className="px-2 py-1.5 text-center border w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mcxClients.map(client => (
                    <tr key={client.mcx_client_id} className="border-b hover:bg-muted/50">
                      <td className="px-2 py-1 border font-medium">{client.client_name}</td>
                      <td className="px-2 py-1 border text-amber-600">{client.id_name}</td>
                      <td className="px-2 py-1 border text-right font-mono">{client.brokerage_per_lot}</td>
                      <td className="px-2 py-1 border text-right">{client.brokerage_remittance}%</td>
                      <td className="px-2 py-1 border text-right">{client.patti_percent}%</td>
                      <td className="px-2 py-1 border text-right font-mono">{client.limit?.toLocaleString()}</td>
                      <td className="px-2 py-1 border">{mainClients.find(c => c.client_id === client.linked_client_id)?.name || '-'}</td>
                      <td className="px-2 py-1 border text-center"><StatusBadge status={client.status} /></td>
                      <td className="px-2 py-1 border text-center">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openClientDialog('edit', client)}>
                          <Edit className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {mcxClients.length === 0 && (
                    <tr><td colSpan={9} className="px-2 py-4 text-center text-muted-foreground">No clients found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </GroupBox>
        </TabsContent>

        {/* Trades Tab */}
        <TabsContent value="trades" className="flex-1 mt-3">
          <GroupBox label="Trade Entry">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Select value={tradeFilter.mcx_client_id || 'all'} onValueChange={(v) => setTradeFilter({...tradeFilter, mcx_client_id: v === 'all' ? '' : v})}>
                  <SelectTrigger className="h-8 w-40"><SelectValue placeholder="All Clients" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {mcxClients.map(c => <SelectItem key={c.mcx_client_id} value={c.mcx_client_id}>{c.client_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={tradeFilter.script_id || 'all'} onValueChange={(v) => setTradeFilter({...tradeFilter, script_id: v === 'all' ? '' : v})}>
                  <SelectTrigger className="h-8 w-36"><SelectValue placeholder="All Scripts" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Scripts</SelectItem>
                    {scripts.filter(s => s.status === 'ACTIVE').map(s => <SelectItem key={s.script_id} value={s.script_id}>{s.script_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={tradeFilter.is_settled || 'all'} onValueChange={(v) => setTradeFilter({...tradeFilter, is_settled: v === 'all' ? '' : v})}>
                  <SelectTrigger className="h-8 w-28"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="false">Open</SelectItem>
                    <SelectItem value="true">Settled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => openTradeDialog('add')} data-testid="add-trade-btn">
                  <Plus className="h-4 w-4 mr-1" />New Trade
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportToExcel(trades, 'trades')}>
                  <FileSpreadsheet className="h-4 w-4 mr-1" />Excel
                </Button>
              </div>
            </div>
            <div className="max-h-[calc(100vh-320px)] overflow-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-center border">Date</th>
                    <th className="px-2 py-1.5 text-left border">Client</th>
                    <th className="px-2 py-1.5 text-left border">Script</th>
                    <th className="px-2 py-1.5 text-center border">Type</th>
                    <th className="px-2 py-1.5 text-right border">Rate</th>
                    <th className="px-2 py-1.5 text-right border">Qty</th>
                    <th className="px-2 py-1.5 text-right border">Brokerage</th>
                    <th className="px-2 py-1.5 text-left border">Remarks</th>
                    <th className="px-2 py-1.5 text-center border">Status</th>
                    <th className="px-2 py-1.5 text-center border w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map(trade => (
                    <tr key={trade.trade_id} className={cn("border-b hover:bg-muted/50", trade.is_settled && "bg-muted/30 text-muted-foreground")}>
                      <td className="px-2 py-1 border text-center">{trade.trade_date?.split('T')[0]}</td>
                      <td className="px-2 py-1 border">{mcxClients.find(c => c.mcx_client_id === trade.mcx_client_id)?.client_name || trade.mcx_client_id}</td>
                      <td className="px-2 py-1 border">{trade.script_name}</td>
                      <td className="px-2 py-1 border text-center">
                        <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", 
                          trade.trade_type === 'BUY' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')}>
                          {trade.trade_type}
                        </span>
                      </td>
                      <td className="px-2 py-1 border text-right font-mono">{trade.rate?.toFixed(2)}</td>
                      <td className="px-2 py-1 border text-right">{trade.quantity}</td>
                      <td className="px-2 py-1 border text-right font-mono">{trade.brokerage?.toFixed(2)}</td>
                      <td className="px-2 py-1 border text-muted-foreground">{trade.remarks || '-'}</td>
                      <td className="px-2 py-1 border text-center">
                        {trade.is_settled ? <CheckCircle className="h-4 w-4 text-emerald-500 inline" /> : <span className="text-amber-500">Open</span>}
                      </td>
                      <td className="px-2 py-1 border text-center">
                        {!trade.is_settled && (
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openTradeDialog('edit', trade)}>
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDeleteTrade(trade)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {trades.length === 0 && (
                    <tr><td colSpan={10} className="px-2 py-4 text-center text-muted-foreground">No trades found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </GroupBox>
        </TabsContent>

        {/* Positions Tab */}
        <TabsContent value="positions" className="flex-1 mt-3">
          <GroupBox label="Open Positions & MTM">
            <div className="flex justify-end mb-3">
              <Button variant="outline" size="sm" onClick={() => {
                const data = positions.filter(p => p.net_quantity !== 0).map(p => ({
                  Client: mcxClients.find(c => c.mcx_client_id === p.mcx_client_id)?.client_name,
                  Script: p.script_name, Direction: p.net_quantity > 0 ? 'LONG' : 'SHORT',
                  Quantity: Math.abs(p.net_quantity), AvgPrice: p.avg_price, CurrentPrice: p.current_price,
                  MTM: p.mtm
                }));
                exportToExcel(data, 'positions');
              }}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Excel
              </Button>
            </div>
            <div className="max-h-[calc(100vh-320px)] overflow-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left border">Client</th>
                    <th className="px-2 py-1.5 text-left border">Script</th>
                    <th className="px-2 py-1.5 text-center border">Direction</th>
                    <th className="px-2 py-1.5 text-right border">Buy Qty</th>
                    <th className="px-2 py-1.5 text-right border">Buy Avg</th>
                    <th className="px-2 py-1.5 text-right border">Sell Qty</th>
                    <th className="px-2 py-1.5 text-right border">Sell Avg</th>
                    <th className="px-2 py-1.5 text-right border">Net Qty</th>
                    <th className="px-2 py-1.5 text-right border">Avg Price</th>
                    <th className="px-2 py-1.5 text-right border">Current</th>
                    <th className="px-2 py-1.5 text-right border">MTM</th>
                    <th className="px-2 py-1.5 text-center border w-20">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.filter(p => p.net_quantity !== 0).map(pos => (
                    <tr key={pos.position_id} className="border-b hover:bg-muted/50">
                      <td className="px-2 py-1 border">{pos.client_name || mcxClients.find(c => c.mcx_client_id === pos.mcx_client_id)?.client_name}</td>
                      <td className="px-2 py-1 border font-medium">{pos.script_name}</td>
                      <td className="px-2 py-1 border text-center">
                        <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center justify-center gap-0.5",
                          pos.net_quantity > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')}>
                          {pos.net_quantity > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {pos.net_quantity > 0 ? 'LONG' : 'SHORT'}
                        </span>
                      </td>
                      <td className="px-2 py-1 border text-right">{pos.buy_quantity}</td>
                      <td className="px-2 py-1 border text-right font-mono">{pos.buy_avg_price?.toFixed(2)}</td>
                      <td className="px-2 py-1 border text-right">{pos.sell_quantity}</td>
                      <td className="px-2 py-1 border text-right font-mono">{pos.sell_avg_price?.toFixed(2)}</td>
                      <td className={cn("px-2 py-1 border text-right font-bold", pos.net_quantity > 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {pos.net_quantity}
                      </td>
                      <td className="px-2 py-1 border text-right font-mono">{pos.avg_price?.toFixed(2)}</td>
                      <td className="px-2 py-1 border text-right font-mono text-blue-600">{pos.current_price?.toFixed(2)}</td>
                      <td className={cn("px-2 py-1 border text-right font-mono font-bold", (pos.mtm || 0) >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {(pos.mtm || 0).toFixed(2)}
                      </td>
                      <td className="px-2 py-1 border text-center">
                        <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => openSettleDialog(pos)}>
                          Settle
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {positions.filter(p => p.net_quantity !== 0).length === 0 && (
                    <tr><td colSpan={12} className="px-2 py-4 text-center text-muted-foreground">No open positions</td></tr>
                  )}
                </tbody>
                <tfoot className="bg-muted/50 font-semibold">
                  <tr>
                    <td colSpan={10} className="px-2 py-1.5 text-right border">Total MTM:</td>
                    <td className={cn("px-2 py-1.5 text-right border font-mono", totalMTM >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                      {totalMTM.toFixed(2)}
                    </td>
                    <td className="border"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </GroupBox>
        </TabsContent>

        {/* Book Settlement Tab (renamed from Settlements) */}
        <TabsContent value="settlements" className="flex-1 mt-3">
          <GroupBox label="Book Settlement">
            <div className="flex justify-end mb-3 gap-2">
              <Button variant="outline" size="sm" onClick={() => exportToExcel(settlements, 'settlements')}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => {
                const cols = ['Date', 'Client', 'Script', 'Net Qty', 'Entry', 'Exit', 'Gross P&L', 'Brokerage', 'Patti', 'Net P&L'];
                const data = settlements.map(s => [
                  s.settled_at?.split('T')[0], s.client_name, s.script_name, s.net_quantity,
                  s.avg_entry_price?.toFixed(2), s.settlement_price?.toFixed(2),
                  s.gross_pnl?.toFixed(2), s.total_brokerage?.toFixed(2), s.patti_amount?.toFixed(2), s.net_pnl?.toFixed(2)
                ]);
                exportToPDF('MCX Settlements', cols, data);
              }}>
                <FileText className="h-4 w-4 mr-1" />PDF
              </Button>
            </div>
            <div className="max-h-[calc(100vh-320px)] overflow-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-center border">Date</th>
                    <th className="px-2 py-1.5 text-left border">Client</th>
                    <th className="px-2 py-1.5 text-left border">ID Name</th>
                    <th className="px-2 py-1.5 text-left border">Script</th>
                    <th className="px-2 py-1.5 text-right border">Net Qty</th>
                    <th className="px-2 py-1.5 text-right border">Entry</th>
                    <th className="px-2 py-1.5 text-right border">Exit</th>
                    <th className="px-2 py-1.5 text-right border">Gross P&L</th>
                    <th className="px-2 py-1.5 text-right border">Brokerage</th>
                    <th className="px-2 py-1.5 text-right border">Remit</th>
                    <th className="px-2 py-1.5 text-right border">Patti</th>
                    <th className="px-2 py-1.5 text-right border">Net P&L</th>
                    <th className="px-2 py-1.5 text-center border">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map(s => (
                    <tr key={s.settlement_id} className="border-b hover:bg-muted/50">
                      <td className="px-2 py-1 border text-center">{s.settled_at?.split('T')[0]}</td>
                      <td className="px-2 py-1 border">{s.client_name}</td>
                      <td className="px-2 py-1 border text-amber-600">{s.id_name}</td>
                      <td className="px-2 py-1 border">{s.script_name}</td>
                      <td className={cn("px-2 py-1 border text-right font-mono", s.net_quantity > 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {s.net_quantity}
                      </td>
                      <td className="px-2 py-1 border text-right font-mono">{s.avg_entry_price?.toFixed(2)}</td>
                      <td className="px-2 py-1 border text-right font-mono">{s.settlement_price?.toFixed(2)}</td>
                      <td className={cn("px-2 py-1 border text-right font-mono", (s.gross_pnl || 0) >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {s.gross_pnl?.toFixed(2)}
                      </td>
                      <td className="px-2 py-1 border text-right font-mono">{s.total_brokerage?.toFixed(2)}</td>
                      <td className="px-2 py-1 border text-right font-mono text-blue-600">{s.brokerage_remittance?.toFixed(2)}</td>
                      <td className="px-2 py-1 border text-right font-mono text-amber-600">{s.patti_amount?.toFixed(2)}</td>
                      <td className={cn("px-2 py-1 border text-right font-mono font-bold", (s.net_pnl || 0) >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {s.net_pnl?.toFixed(2)}
                      </td>
                      <td className="px-2 py-1 border text-center"><StatusBadge status={s.settlement_type} /></td>
                    </tr>
                  ))}
                  {settlements.length === 0 && (
                    <tr><td colSpan={13} className="px-2 py-4 text-center text-muted-foreground">No settlements found</td></tr>
                  )}
                </tbody>
                <tfoot className="bg-muted/50 font-semibold">
                  <tr>
                    <td colSpan={7} className="px-2 py-1.5 text-right border">Totals:</td>
                    <td className={cn("px-2 py-1.5 text-right border font-mono", settlements.reduce((s,x) => s + (x.gross_pnl||0), 0) >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                      {settlements.reduce((s,x) => s + (x.gross_pnl||0), 0).toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5 text-right border font-mono">{settlements.reduce((s,x) => s + (x.total_brokerage||0), 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right border font-mono text-blue-600">{settlements.reduce((s,x) => s + (x.brokerage_remittance||0), 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right border font-mono text-amber-600">{settlements.reduce((s,x) => s + (x.patti_amount||0), 0).toFixed(2)}</td>
                    <td className={cn("px-2 py-1.5 text-right border font-mono", settlements.reduce((s,x) => s + (x.net_pnl||0), 0) >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                      {settlements.reduce((s,x) => s + (x.net_pnl||0), 0).toFixed(2)}
                    </td>
                    <td className="border"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </GroupBox>
        </TabsContent>

        {/* Weekly$ Tab */}
        <TabsContent value="weekly-dollar" className="flex-1 mt-0 -mx-3">
          <WeeklyDollarPage />
        </TabsContent>

        {/* MCX Settlement Tab */}
        <TabsContent value="mcx-settlement" className="flex-1 mt-3">
          <MCXSettlementPage />
        </TabsContent>

        {/* Reports Tab (Placeholder) */}
        <TabsContent value="reports" className="flex-1 mt-3">
          <div className="flex flex-col items-center justify-center h-64 bg-card border rounded" data-testid="reports-placeholder">
            <BarChart3 className="h-12 w-12 text-muted-foreground mb-3" />
            <h3 className="text-lg font-medium text-muted-foreground">Reports</h3>
            <p className="text-sm text-muted-foreground mt-1">Combined Report Builder coming soon</p>
          </div>
        </TabsContent>

        {/* Agent Settings Tab (Placeholder) */}
        <TabsContent value="agent-settings" className="flex-1 mt-3">
          <div className="space-y-4">
            {/* Bhav Copy Section */}
            <div className="grid grid-cols-3 gap-4">
              {/* Status Card */}
              <GroupBox label="Bhav Copy Status">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Status</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-500">Active</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Last Update</span>
                    <span className="text-xs font-mono">{bhavCopyStatus?.last_update?.split('T')[0] || 'Never'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Source</span>
                    <span className="text-xs">{bhavCopyStatus?.last_source || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Today's Updates</span>
                    <span className="text-xs font-mono">{bhavCopyStatus?.today_updates || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Active Scripts</span>
                    <span className="text-xs font-mono">{bhavCopyStatus?.active_scripts || scripts.filter(s => s.status === 'ACTIVE').length}</span>
                  </div>
                  <div className="pt-2 border-t space-y-2">
                    <Button 
                      size="sm" 
                      className="w-full" 
                      onClick={() => setBhavCopyDialog({ open: true })}
                      data-testid="upload-bhav-copy-btn"
                    >
                      <Upload className="h-4 w-4 mr-1" />Upload Bhav Copy CSV
                    </Button>
                    {bhavCopyStatus?.can_trigger_scheduled && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="w-full" 
                        onClick={handleTriggerScheduled}
                        data-testid="trigger-scheduled-btn"
                      >
                        <Play className="h-4 w-4 mr-1" />Trigger Scheduled Update
                      </Button>
                    )}
                  </div>
                </div>
              </GroupBox>

              {/* Quick Price Update */}
              <GroupBox label="Quick Price Update">
                <div className="space-y-2 max-h-[300px] overflow-auto">
                  {scripts.filter(s => s.status === 'ACTIVE').map(script => (
                    <div key={script.script_id} className="flex items-center gap-2">
                      <span className="text-xs w-24 truncate">{script.script_name}</span>
                      <span className="text-xs text-muted-foreground w-16 text-right font-mono">
                        {script.last_price?.toFixed(2) || '0.00'}
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        className="h-7 w-24 text-xs"
                        placeholder="New price"
                        onChange={(e) => {
                          const updated = scripts.map(s => 
                            s.script_id === script.script_id ? {...s, _newPrice: e.target.value} : s
                          );
                          setScripts(updated);
                        }}
                      />
                    </div>
                  ))}
                  {scripts.filter(s => s.status === 'ACTIVE').length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">No active scripts</p>
                  )}
                </div>
                {scripts.filter(s => s.status === 'ACTIVE').length > 0 && (
                  <Button size="sm" className="w-full mt-3" onClick={handleBulkPriceUpdate}>
                    Update Prices
                  </Button>
                )}
              </GroupBox>

              {/* Price History */}
              <GroupBox label="Price Update History">
                <div className="space-y-1 max-h-[350px] overflow-auto">
                  {priceHistory.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b">
                      <div>
                        <span className="font-medium">{h.script_name}</span>
                        <span className="text-muted-foreground ml-2">{h.source}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-mono">{h.old_price?.toFixed(2)} → {h.new_price?.toFixed(2)}</span>
                        <div className={cn("text-[10px]", h.new_price > h.old_price ? 'text-emerald-500' : 'text-red-500')}>
                          {h.new_price > h.old_price ? '+' : ''}{((h.new_price - h.old_price) / h.old_price * 100).toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  ))}
                  {priceHistory.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">No price history</p>
                  )}
                </div>
              </GroupBox>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Script Dialog */}
      <Dialog open={scriptDialog.open} onOpenChange={(open) => !open && setScriptDialog({...scriptDialog, open: false})}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{scriptDialog.mode === 'add' ? 'Add Script' : 'Edit Script'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            <div>
              <Label className="text-xs">Script Name *</Label>
              <Input value={scriptForm.script_name} onChange={(e) => setScriptForm({...scriptForm, script_name: e.target.value.toUpperCase()})} className="h-8" placeholder="e.g., GOLD" />
            </div>
            <div>
              <Label className="text-xs">Exchange *</Label>
              <Select value={scriptForm.exchange} onValueChange={(v) => setScriptForm({...scriptForm, exchange: v})}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXCHANGES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Contract Name *</Label>
              <Input value={scriptForm.contract_name} onChange={(e) => setScriptForm({...scriptForm, contract_name: e.target.value})} className="h-8" placeholder="e.g., GOLDM JUN 2026" />
            </div>
            <div>
              <Label className="text-xs">Expiry Date *</Label>
              <Input type="date" value={scriptForm.expiry_date} onChange={(e) => setScriptForm({...scriptForm, expiry_date: e.target.value})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Lot Size *</Label>
              <Input type="number" min="1" value={scriptForm.lot_size} onChange={(e) => setScriptForm({...scriptForm, lot_size: parseInt(e.target.value) || 1})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Tick Size</Label>
              <Input type="number" step="0.01" value={scriptForm.tick_size} onChange={(e) => setScriptForm({...scriptForm, tick_size: parseFloat(e.target.value) || 0.01})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Last Price</Label>
              <Input type="number" step="0.01" value={scriptForm.last_price} onChange={(e) => setScriptForm({...scriptForm, last_price: parseFloat(e.target.value) || 0})} className="h-8" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScriptDialog({...scriptDialog, open: false})}>Cancel</Button>
            <Button onClick={handleSaveScript}>{scriptDialog.mode === 'add' ? 'Create' : 'Update'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Dialog */}
      <Dialog open={clientDialog.open} onOpenChange={(open) => !open && setClientDialog({...clientDialog, open: false})}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{clientDialog.mode === 'add' ? 'Add MCX Client' : 'Edit MCX Client'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            <div>
              <Label className="text-xs">Client Name *</Label>
              <Input value={clientForm.client_name} onChange={(e) => setClientForm({...clientForm, client_name: e.target.value})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">ID Name * (for Ledger)</Label>
              <Input value={clientForm.id_name} onChange={(e) => setClientForm({...clientForm, id_name: e.target.value})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Brokerage / Lot</Label>
              <Input type="number" step="0.01" value={clientForm.brokerage_per_lot} onChange={(e) => setClientForm({...clientForm, brokerage_per_lot: parseFloat(e.target.value) || 0})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Remittance %</Label>
              <Input type="number" step="0.1" min="0" max="100" value={clientForm.brokerage_remittance} onChange={(e) => setClientForm({...clientForm, brokerage_remittance: parseFloat(e.target.value) || 0})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Patti %</Label>
              <Input type="number" step="0.1" min="0" max="100" value={clientForm.patti_percent} onChange={(e) => setClientForm({...clientForm, patti_percent: parseFloat(e.target.value) || 0})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Limit</Label>
              <Input type="number" value={clientForm.limit} onChange={(e) => setClientForm({...clientForm, limit: parseFloat(e.target.value) || 0})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Contact Number</Label>
              <Input value={clientForm.contact_number} onChange={(e) => setClientForm({...clientForm, contact_number: e.target.value})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Link to Main Client</Label>
              <Select value={clientForm.linked_client_id || 'none'} onValueChange={(v) => setClientForm({...clientForm, linked_client_id: v === 'none' ? '' : v})}>
                <SelectTrigger className="h-8"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {mainClients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientDialog({...clientDialog, open: false})}>Cancel</Button>
            <Button onClick={handleSaveClient}>{clientDialog.mode === 'add' ? 'Create' : 'Update'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trade Dialog */}
      <Dialog open={tradeDialog.open} onOpenChange={(open) => !open && setTradeDialog({...tradeDialog, open: false})}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{tradeDialog.mode === 'add' ? 'New Trade' : 'Edit Trade'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            <div>
              <Label className="text-xs">Trade Date *</Label>
              <Input type="date" value={tradeForm.trade_date} onChange={(e) => setTradeForm({...tradeForm, trade_date: e.target.value})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Client *</Label>
              <Select value={tradeForm.mcx_client_id} onValueChange={(v) => setTradeForm({...tradeForm, mcx_client_id: v})}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {mcxClients.filter(c => c.status === 'ACTIVE').map(c => <SelectItem key={c.mcx_client_id} value={c.mcx_client_id}>{c.client_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Script *</Label>
              <Select value={tradeForm.script_id} onValueChange={(v) => setTradeForm({...tradeForm, script_id: v})}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {scripts.filter(s => s.status === 'ACTIVE').map(s => <SelectItem key={s.script_id} value={s.script_id}>{s.script_name} ({s.contract_name})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Trade Type *</Label>
              <Select value={tradeForm.trade_type} onValueChange={(v) => setTradeForm({...tradeForm, trade_type: v})}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRADE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Rate *</Label>
              <Input type="number" step="0.01" value={tradeForm.rate} onChange={(e) => setTradeForm({...tradeForm, rate: e.target.value})} className="h-8" placeholder="0.00" />
            </div>
            <div>
              <Label className="text-xs">Quantity (Lots) *</Label>
              <Input type="number" min="1" value={tradeForm.quantity} onChange={(e) => setTradeForm({...tradeForm, quantity: e.target.value})} className="h-8" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Remarks</Label>
              <Input value={tradeForm.remarks} onChange={(e) => setTradeForm({...tradeForm, remarks: e.target.value})} className="h-8" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTradeDialog({...tradeDialog, open: false})}>Cancel</Button>
            <Button onClick={handleSaveTrade}>{tradeDialog.mode === 'add' ? 'Create' : 'Update'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settlement Dialog */}
      <Dialog open={settleDialog.open} onOpenChange={(open) => !open && setSettleDialog({...settleDialog, open: false})}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Settle Position</DialogTitle>
            <DialogDescription>
              {settleDialog.position && `${settleDialog.position.client_name} - ${settleDialog.position.script_name} (${settleDialog.position.net_quantity > 0 ? 'LONG' : 'SHORT'} ${Math.abs(settleDialog.position.net_quantity)} lots)`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <Label className="text-xs">Settlement Price *</Label>
              <Input type="number" step="0.01" value={settleForm.settlement_price} onChange={(e) => setSettleForm({...settleForm, settlement_price: e.target.value})} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Settlement Type</Label>
              <Select value={settleForm.settlement_type} onValueChange={(v) => setSettleForm({...settleForm, settlement_type: v})}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MANUAL">Manual</SelectItem>
                  <SelectItem value="EXPIRY">Expiry</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleDialog({...settleDialog, open: false})}>Cancel</Button>
            <Button onClick={handleSettle}>Settle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price Update Dialog */}
      <Dialog open={priceDialog.open} onOpenChange={(open) => !open && setPriceDialog({...priceDialog, open: false})}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Price: {priceDialog.script?.script_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-xs">New Price *</Label>
            <Input type="number" step="0.01" value={priceForm.price} onChange={(e) => setPriceForm({price: e.target.value})} className="h-8" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceDialog({...priceDialog, open: false})}>Cancel</Button>
            <Button onClick={handleUpdatePrice}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bhav Copy Upload Dialog */}
      <Dialog open={bhavCopyDialog.open} onOpenChange={(open) => !open && setBhavCopyDialog({ open: false })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Bhav Copy CSV</DialogTitle>
            <DialogDescription>
              Paste the content of your Bhav Copy CSV file. Expected format: Symbol, Expiry Date, Open, High, Low, Close, Volume, OI
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-xs mb-2 block">CSV Content</Label>
            <textarea
              className="w-full h-64 p-2 text-xs font-mono bg-muted border rounded resize-none"
              placeholder="Symbol,Expiry Date,Open,High,Low,Close,Volume,OI
GOLD,30-Jun-2026,52000,52500,51800,52400,15000,45000
SILVER,30-Jun-2026,68500,69000,68200,68900,8000,25000"
              value={csvContent}
              onChange={(e) => setCsvContent(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-2">
              The system will match symbols with your active scripts and update their last prices.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCsvContent(''); setBhavCopyDialog({ open: false }); }}>Cancel</Button>
            <Button onClick={handleBhavCopyUpload}>
              <Upload className="h-4 w-4 mr-1" />Process & Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
